import React from 'react';
import { getThemeClasses } from '../utils/themeUtils';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Log the error to console and potentially to an error reporting service
    console.error('ErrorBoundary caught an error:', {
      errorId,
      error,
      errorInfo,
      props: this.props,
      retryCount: this.state.retryCount
    });
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // Report error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo, errorId);
    }
  }

  reportError = (error, errorInfo, errorId) => {
    // In a real application, you would send this to an error tracking service
    // like Sentry, LogRocket, or Bugsnag
    const errorReport = {
      errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.props.userId || 'anonymous',
      retryCount: this.state.retryCount,
      props: {
        component: this.props.component || 'unknown',
        context: this.props.context || {}
      }
    };

    // Example: Send to error tracking service
    // errorTrackingService.captureException(errorReport);
    
    console.log('Error report generated:', errorReport);
  };

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    // Limit retry attempts to prevent infinite loops
    if (newRetryCount > 3) {
      this.handleReload();
      return;
    }

    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      retryCount: newRetryCount
    });

    // Call custom retry handler if provided
    if (this.props.onRetry) {
      this.props.onRetry(newRetryCount);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleReportIssue = () => {
    const { errorId, error } = this.state;
    const subject = encodeURIComponent(`Error Report: ${errorId}`);
    const body = encodeURIComponent(`
Error ID: ${errorId}
Error Message: ${error?.message || 'Unknown error'}
URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}

Please describe what you were doing when this error occurred:
[Your description here]
    `);
    
    // Open email client or support form
    const supportEmail = 'support@parrotmate.com'; // Replace with actual support email
    window.open(`mailto:${supportEmail}?subject=${subject}&body=${body}`);
  };

  getErrorSeverity = () => {
    const { error } = this.state;
    
    // Determine error severity based on error type
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      return 'low'; // Network/deployment related
    }
    
    if (error?.name === 'TypeError' && error?.message?.includes('Cannot read property')) {
      return 'medium'; // Likely data structure issue
    }
    
    if (error?.message?.includes('Network Error') || error?.message?.includes('fetch')) {
      return 'low'; // Network related
    }
    
    return 'high'; // Unknown or critical error
  };

  getErrorMessage = () => {
    const { error } = this.state;
    const severity = this.getErrorSeverity();
    
    if (severity === 'low') {
      return 'A temporary issue occurred. This is usually resolved by refreshing the page.';
    }
    
    if (severity === 'medium') {
      return 'An unexpected error occurred. Please try again or refresh the page.';
    }
    
    return this.props.fallbackMessage || 'A critical error occurred. Please refresh the page or contact support if the issue persists.';
  };

  render() {
    if (this.state.hasError) {
      const theme = getThemeClasses();
      const { retryCount, errorId } = this.state;
      const severity = this.getErrorSeverity();
      const errorMessage = this.getErrorMessage();
      
      // Fallback UI
      return (
        <div className={`min-h-[400px] flex items-center justify-center p-4 ${this.props.className || ''}`}>
          <div className={`${theme.bg.primary} border ${theme.border.error} rounded-lg p-8 max-w-md w-full shadow-lg`}>
            <div className="text-center">
              {/* Error Icon */}
              <div className={`${theme.text.error} mb-4`}>
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              {/* Error Title */}
              <h3 className={`text-lg font-semibold ${theme.text.primary} mb-2`}>
                Something went wrong
              </h3>
              
              {/* Error Message */}
              <p className={`${theme.text.secondary} mb-6`}>
                {errorMessage}
              </p>
              
              {/* Error ID for support */}
              {errorId && (
                <p className={`text-xs ${theme.text.muted} mb-4 font-mono`}>
                  Error ID: {errorId}
                </p>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-3">
                {retryCount < 3 && (
                  <button
                    onClick={this.handleRetry}
                    className={`w-full text-white px-4 py-2 rounded-lg font-medium transition-colors ${theme.bg.button.primary}`}
                  >
                    Try Again {retryCount > 0 && `(${retryCount}/3)`}
                  </button>
                )}
                
                <button
                  onClick={this.handleReload}
                  className={`w-full border ${theme.border.secondary} ${theme.text.secondary} hover:${theme.bg.secondary} px-4 py-2 rounded-lg font-medium transition-colors`}
                >
                  Refresh Page
                </button>
                
                {severity === 'high' && (
                  <button
                    onClick={this.handleReportIssue}
                    className={`w-full text-sm ${theme.text.muted} hover:${theme.text.secondary} px-4 py-2 transition-colors`}
                  >
                    Report Issue
                  </button>
                )}
              </div>
              
              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-6 text-left">
                  <summary className={`text-sm ${theme.text.muted} cursor-pointer hover:${theme.text.secondary}`}>
                    Error Details (Development)
                  </summary>
                  <div className={`mt-2 p-3 ${theme.bg.secondary} rounded text-xs ${theme.text.error} overflow-auto max-h-40`}>
                    <div className="font-semibold mb-2">Error:</div>
                    <pre className="whitespace-pre-wrap text-xs">{this.state.error.toString()}</pre>
                    {this.state.errorInfo && (
                      <>
                        <div className="font-semibold mt-4 mb-2">Component Stack:</div>
                        <pre className="whitespace-pre-wrap text-xs">{this.state.errorInfo.componentStack}</pre>
                      </>
                    )}
                    <div className="font-semibold mt-4 mb-2">Error ID:</div>
                    <pre className="whitespace-pre-wrap text-xs">{errorId}</pre>
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
